import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DominioGrupoTuss } from '@gestor-web/@core/types';
import { normalizePerfil } from '@gestor-web/@core/utils';
import { HEADER_SKIP_LOAD_INTERCEPTOR } from '@gestor-web/app/shared/services';
import { environment } from '@gestor-web/environments/environment';
import { map } from 'rxjs/operators';
import { Api } from '../api';
import {
  ApiResponse,
  CampoCriticaAns,
  CategoriaServicoItem,
  Cbo,
  ConfigParametro,
  CriticaDetalhe,
  Dominio,
  GridVisao,
  ItensProcedimentosTuss,
  ItensProcedimentosTussParams,
  LancamentosRejeitadosOutrasDespesasFiltro,
  MetodoCalculoIndicador,
  MotivoGlosa,
  NomeCategoriaServico,
  Notificacao,
  Orientacao,
  PAGE_PARAMS_DEFAULT,
  PageParams,
  QuantidadeRemessaContratada,
  RegistroErro,
  TabelaTuss,
  TermosDominios,
  TipoParametro,
  Total,
  UnidadeMedida,
  UsuarioFuncionalidade,
  UsuarioPerfil,
} from '../ords/model';

const MODULE_PATH = '/geral';

@Injectable({ providedIn: 'root' })
export class GeralGoApi extends Api {
  constructor(http: HttpClient) {
    super(`${environment.API_GO_BASE_PATH}${MODULE_PATH}`, http);
  }

  fetchPerfilCorrente() {
    return this.get<UsuarioPerfil>(`/perfil`).pipe(map(perfil => normalizePerfil(perfil)));
  }

  updateUltimoAcesso() {
    return this.post<void>(`/perfil/ultimo_acesso`);
  }

  criarPerfilUsuario(idss: UsuarioPerfil) {
    return this.post<UsuarioPerfil>(`/perfil`, idss).pipe(map(perfil => normalizePerfil(perfil)));
  }

  atualizarPerfilUsuario(idss: UsuarioPerfil) {
    return this.put<UsuarioPerfil>(`/perfil`, idss).pipe(map(perfil => normalizePerfil(perfil)));
  }

  /**
   * Funcionalidades do usuário autenticado.
   *
   * A operadora é opcional: sem ela o back-end devolve apenas as funcionalidades concedidas
   * pelo papel de acesso; com ela, soma as dos módulos contratados pela operadora. É por isso
   * que quem não tem vínculo — Básico, e o gestor-admin, que não tem workspace — consegue
   * carregar a lista.
   */
  fetchUsuarioFuncionalidades(regans?: string) {
    const params = this.trimParams({ operadora: regans });
    return this.http.get<ApiResponse<UsuarioFuncionalidade>>(`${this.basePath}/usuario/funcionalidades`, {
      params,
      headers: { [HEADER_SKIP_LOAD_INTERCEPTOR]: 'true' },
    });
  }

  fetchNomeCategoriaServico() {
    return this.get<ApiResponse<NomeCategoriaServico>>(`/analises/nome-categoria-servico`);
  }

  fetchCategoriasServicoNaoHospitalar() {
    return this.get<ApiResponse<CategoriaServicoItem>>(
      `/analises/redimensionamento-rede-nao-hospitalar/categorias-servico`,
    );
  }

  getZendeskJwtToken() {
    return this.get<{ token: string }>(`/jwt_sign_zendesk`);
  }

  fetchLancamentosRejeitadosOutrasDespesasFiltro() {
    return this.get<ApiResponse<LancamentosRejeitadosOutrasDespesasFiltro>>(`/ptiss/motivo-glossa`);
  }

  /** Fora do interceptor de carga: o topbar pede o parâmetro no login, e o redirecionamento inicial o cancelaria. */
  fetchParametroConfig(cd_parametro: TipoParametro) {
    return this.http
      .get<ApiResponse<ConfigParametro>>(`${this.basePath}/config/parametro/${cd_parametro}`, {
        headers: { [HEADER_SKIP_LOAD_INTERCEPTOR]: 'true' },
      })
      .pipe(map(response => response.items[0].vl_parametro));
  }

  fetchDetalheCritica(cd_critica: number) {
    return this.get<ApiResponse<CriticaDetalhe>>(`/criticas`, { cd_critica });
  }

  fetchOrientacao(cd_orientacao: string) {
    return this.get<Orientacao>(`/config/orientacoes/${cd_orientacao}`);
  }

  fetchDominios() {
    return this.get<ApiResponse<Dominio>>('/config/dominios').pipe(map(resp => resp.items));
  }

  fetchMetodoCalculoIndicador(cd_indicador: string) {
    return this.get<MetodoCalculoIndicador>(`/indicadores/${cd_indicador}/metodo-calculo-proposicoes`);
  }

  fetchGridVisoes() {
    // console.log('[Grid] fetchGridVisoes');
    return this.get<ApiResponse<GridVisao>>(`/config/visoes`);
  }

  createGridVisao(visao: GridVisao) {
    // console.log('[Grid] createGridVisao', visao);
    return this.post<GridVisao>(`/config/visoes`, visao);
  }

  updateGridVisao(visao: GridVisao) {
    // console.log('[Grid] updateGridVisao', visao);
    return this.put<void>(`/config/visoes/${visao.cd_visao}`, visao);
  }

  deleteGridVisao(visao: GridVisao) {
    // console.log('[Grid] deleteGridVisao', visao);
    return this.delete(`/config/visoes/${visao.cd_visao}`);
  }

  reorderGridVisoes(visoes: GridVisao[]) {
    // console.log('[Grid] reorderGridVisoes', visoes);
    const body = {
      cd_visoes: visoes.map(v => v.cd_visao),
    };
    return this.put<void>(`/config/visoes`, body);
  }

  fetchNotificacoes(params?: NotificacaoParams) {
    // console.log('[Geral] fetchNotificacoes', params);
    const trimmed = this.trimParams(params);
    // Polling do topbar: fora do interceptor, não liga o spinner nem é cancelado pela navegação.
    return this.http.get<ApiResponse<Notificacao>>(`${this.basePath}/notificacoes`, {
      params: trimmed,
      headers: { [HEADER_SKIP_LOAD_INTERCEPTOR]: 'true' },
    });
  }

  marcarNotificacaoLida(notificacao: Notificacao) {
    // console.log('[Geral] marcarNotificacaoLido', notificacao);
    return this.post<void>(`/notificacoes/${notificacao.cd_notificacao}/lida`);
  }

  marcarNotificacaoArquivada(notificacao: Notificacao) {
    // console.log('[Geral] marcarNotificacaoArquivada', notificacao);
    return this.post<void>(`/notificacoes/${notificacao.cd_notificacao}/arquiva`);
  }

  registrarErro(erro: RegistroErro) {
    const trimmed = this.trimParams(erro);
    return this.http.post<void>(`${this.basePath}/erros`, trimmed, {
      headers: { [HEADER_SKIP_LOAD_INTERCEPTOR]: 'true' },
    });
  }

  fetchQuantidadeRemessaContratada(cd_modulo: string) {
    return this.get<ApiResponse<QuantidadeRemessaContratada>>(
      `/arquivos/quantidade-remessa-contratada/${cd_modulo}`,
    );
  }

  fetchMotivosGlosa() {
    return this.get<ApiResponse<MotivoGlosa>>('/ptiss/motivos-glosa');
  }

  fetchCamposCriticas() {
    return this.get<ApiResponse<CampoCriticaAns>>('/ptiss/campos-criticas');
  }

  /**
   * Busca os dados complementares da critica ANS pelo código da crítica.
   */
  fetchCamposByNrCampo(nr_campo?: number) {
    const params = {};
    if (nr_campo) {
      params['nr_campo'] = nr_campo;
    }
    return this.get<ApiResponse<CampoCriticaAns>>('/ptiss/campos', params);
  }

  fetchCBOs() {
    return this.get<ApiResponse<Cbo>>('/ptiss/cbos');
  }

  fetchDominioGrupoTuss() {
    return this.get<ApiResponse<DominioGrupoTuss>>('/ptiss/grupos-tuss').pipe(map(resp => resp.items));
  }

  fetchTabelas(params?: Partial<Pick<TabelaTuss, 'ib_tuss'>>) {
    const trimmed = this.trimParams(params);
    return this.get<ApiResponse<TabelaTuss>>('/ptiss/tabelas', trimmed);
  }

  fetchTermosDominios() {
    return this.get<ApiResponse<TermosDominios>>('/ptiss/termos-dominios');
  }

  fetchItensProcedimentosTuss(params: ItensProcedimentosTussParams, page: PageParams = PAGE_PARAMS_DEFAULT) {
    const trimmed = this.trimParams(params);
    return this.get<ApiResponse<ItensProcedimentosTuss>>('/ptiss/itens-procedimentos-tuss', {
      ...trimmed,
      page: Math.floor(page.offset / page.limit),
      size: page.limit,
    });
  }

  countItensProcedimentosTuss(params: ItensProcedimentosTussParams) {
    const trimmed = this.trimParams(params);
    return this.get<Total>('/ptiss/itens-procedimentos-tuss/conta_total', trimmed);
  }

  fetchUnidadesMedida() {
    return this.get<ApiResponse<UnidadeMedida>>('/ptiss/unidades-medida');
  }
}

export interface NotificacaoParams {
  ib_arquivado?: number;
}
